/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import workerCode from './worker-code';

export interface ColumnSuggestions {
    [column: string]: string[];
}

/**
 * Service to manage WebWorker lifecycle for data processing
 * Implements one-shot worker pattern: create -> use -> terminate
 */
@Injectable({
    providedIn: 'root',
})
export class WorkerManagerService {
    /**
     * Calculate popular values (top 10) for specified columns using a WebWorker
     * @param data Array of data items to analyze
     * @param columns Array of column paths to analyze (supports nested paths like 'data.shipper_name')
     * @returns Observable that emits the suggestions and completes
     */
    calculatePopularValues(data: any[], columns: string[]): Observable<ColumnSuggestions> {
        return new Observable(observer => {
            // Check if Web Workers are supported
            if (typeof Worker === 'undefined') {
                console.warn('[WorkerManager] Web Workers not supported, skipping suggestions calculation');
                observer.next({});
                observer.complete();
                return;
            }

            try {
                // Create worker from blob (workaround for dev server CORS issues)
                const workerBlob = new Blob([this.getWorkerCode()], { type: 'application/javascript' });
                const workerUrl = URL.createObjectURL(workerBlob);
                const worker = new Worker(workerUrl);

                // Handle successful completion
                worker.onmessage = ({ data: suggestions }: MessageEvent<ColumnSuggestions>) => {
                    observer.next(suggestions);
                    observer.complete();
                    worker.terminate(); // Clean up worker
                    URL.revokeObjectURL(workerUrl); // Clean up blob URL
                };

                // Handle errors
                worker.onerror = (error) => {
                    console.error('[WorkerManager] Worker error:', error);
                    observer.error(error);
                    worker.terminate(); // Clean up worker even on error
                    URL.revokeObjectURL(workerUrl); // Clean up blob URL
                };

                // Send work to worker
                const message = { data, columns };
                worker.postMessage(message);

            } catch (error) {
                console.error('[WorkerManager] Failed to create worker:', error);
                observer.error(error);
            }
        });
    }

    /**
     * Returns the worker code as a string for blob creation
     * This is a workaround for CORS issues in development
     */
    private getWorkerCode(): string {
        return workerCode
    }
}
